
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";

import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import { callAPI, Method, handleApiError, updateMainHeader } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { HeaderList, IHeaderListActions } from "../../Components/Header/HeaderList";

// Interfaces
interface IVendorOption {
  id: string;
  vendorName: string;
}

interface IPurchaseItemForm {
  itemName: string;
  qty: number | string;
  rate: number | string;
  tax: number | string;
  createdAt?: string;
  id?: string;
}

interface IPurchaseOrderForm {
  vendorID: string;
  billDate: string;
  dueDate: string;
  items: IPurchaseItemForm[];
  subtotal: number | string;
  taxAmount: number | string;
  totalAmount: number | string;
  billNo?: string;
}

// Helper to calculate totals
const calculateTotals = (items: IPurchaseItemForm[]) => {
  let subtotal = 0;
  let taxAmount = 0;

  items.forEach(item => {
    const qty = Number(item.qty) || 0;
    const rate = Number(item.rate) || 0;
    const tax = Number(item.tax) || 0;

    const itemSubtotal = qty * rate;
    const itemTax = itemSubtotal * (tax / 100);

    subtotal += itemSubtotal;
    taxAmount += itemTax;
  });

  const totalAmount = subtotal + taxAmount;

  return {
    subtotal: subtotal.toFixed(2),
    taxAmount: taxAmount.toFixed(2),
    totalAmount: totalAmount.toFixed(2),
  };
};

export const CreatePurchaseOrder: React.FC<any> = React.memo(() => {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const params = useParams();
  const id = params?.id;
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [vendorOptions, setVendorOptions] = useState<IVendorOption[]>([]);
  const formRef = useRef<HTMLFormElement>(null); // Add form ref

  const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm<IPurchaseOrderForm>({
    defaultValues: {
      vendorID: "",
      billDate: dayjs().format("YYYY-MM-DD"),
      dueDate: dayjs().format("YYYY-MM-DD"),
      items: [{ itemName: "", qty: 1, rate: 0, tax: 0 }],
      subtotal: 0,
      taxAmount: 0,
      totalAmount: 0,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const watchedItems = watch("items");

  // Fetch vendors & edit data
  useEffect(() => {
    const fetchData = async () => {
      setInitialLoading(true);

      try {
        // Fetch vendors
        const response: any = await callAPI("Vendor", Method.GET);
        const vendors: IVendorOption[] = (Array.isArray(response) ? response : response?.data || []).map((v: any) => ({
          id: v.id || v._id,
          vendorName: v.vendorName,
        }));
        setVendorOptions(vendors);
      } catch {
        showMessage("Error fetching vendors", "error");
      }

      // Fetch existing order if edit mode
      if (isEditMode && id) {
        try {
          const response: any = await callAPI("PurchaseOrder", Method.GET, null, id);
          const orderData = response?.data?.[0] || response?.purchaseOrder?.[0] || response;

          if (orderData) {
            const sortedItems = [...(orderData.items || [])].sort((a: any, b: any) => {
              if (a.createdAt && b.createdAt) {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
              }
              return 0;
            });

            reset({
              vendorID: orderData.vendorID || orderData.vendorId || "",
              billDate: orderData.billDate ? dayjs(orderData.billDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
              dueDate: orderData.dueDate ? dayjs(orderData.dueDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
              billNo: orderData.billNo || "",
              items: sortedItems.map((i: any) => ({
                itemName: i.itemName || "",
                qty: i.qty || 0,
                rate: i.rate || 0,
                tax: i.tax || 0,
                createdAt: i.createdAt,
                id: i.id || i._id,
              })) || [{ itemName: "", qty: 1, rate: 0, tax: 0 }],
              subtotal: orderData.subtotal || 0,
              taxAmount: orderData.taxAmount || 0,
              totalAmount: orderData.totalAmount || 0,
            });
          }
        } catch {
          showMessage("Error fetching Purchase Order", "error");
        }
      }

      setInitialLoading(false);
    };

    fetchData();
  }, [id, isEditMode, reset, showMessage]);

  // Update totals when items change
  useEffect(() => {
    const { subtotal, taxAmount, totalAmount } = calculateTotals(watchedItems || []);
    setValue("subtotal", subtotal);
    setValue("taxAmount", taxAmount);
    setValue("totalAmount", totalAmount);
  }, [watchedItems, setValue]);

  // Update header & breadcrumbs
  useEffect(() => {
    const title = isEditMode ? "Edit Purchase Order" : "Create Purchase Order";
    updateMainHeader(title);
    breadCrumbsStore.updateBreadCrumbs([
      { "Purchase Orders": "/purchase/list" },
      { [title]: "" },
    ]);
  }, [isEditMode]);

  // **Handle Enter key submission**
  useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        formRef.current?.requestSubmit();
      }
    };
    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, []);

  const handleCancel = () => navigate("/purchase/list");

  const onSubmit = async (data: IPurchaseOrderForm) => {
    const formattedbillDate: any = dayjs(`${data?.billDate} 00:00`, "DD-MM-YYYY HH:mm").toISOString();
    const formatteddueDate: any = dayjs(`${data?.dueDate} 00:00`, "DD-MM-YYYY HH:mm").toISOString();

    setLoading(true);

    const payload = {
      vendorID: data.vendorID || null,

      billDate: formattedbillDate,
      dueDate: formatteddueDate,
      items: data.items.map(i => ({
        itemName: i.itemName,
        qty: Number(i.qty) || 0,
        rate: Number(i.rate) || 0,
        tax: Number(i.tax) || 0,
      })),
      subtotal: Number(data.subtotal),
      taxAmount: Number(data.taxAmount),
      totalAmount: Number(data.totalAmount),
      billNo: data.billNo,
    };
    

    try {
      const method = isEditMode ? Method.PUT : Method.POST;
      const apiId = isEditMode ? id : null;
      const url = isEditMode ? `PurchaseOrder` : `PurchaseOrder?vendorId=${data.vendorID}`

      const response: any = await callAPI(url, method, payload, apiId);

      if (response?.error || response?.errors) {
        showMessage(handleApiError(response), "error");
      } else {
        showMessage(isEditMode ? "Updated successfully" : "Added successfully", "success");
        navigate("/purchase/list");
      }
    } catch {
      showMessage("Error submitting Purchase Order", "error");
    } finally {
      setLoading(false);
    }
  };

  const actions: IHeaderListActions[] = [
    { type: "button", text: "Cancel", onClick: handleCancel, size: "small", variant: "outlined", color: "error" },
    { type: "button", text: "Save", loading, buttonType: "submit", size: "small", variant: "outlined", color: "primary" },
  ];

  if (initialLoading) {
    return (
      <Paper elevation={3} sx={{ p: 3, display: "flex", justifyContent: "center", mt: 2 }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <HeaderList
        title={isEditMode ? "Update Purchase Order" : "Add Purchase Order"}
        isBackButton
        url="/purchase/list"
        type="card"
        actions={actions}
        isDivider
        headerSx={{ height: 50 }}
      />

      <Paper elevation={3} sx={{ p: 3, mt: 1 }}>
        <Typography sx={{ fontSize: '.9rem' }} color={'gray'}>PurchaseOrder Detailes</Typography><br></br>

        <Grid container spacing={2} >
          {/* Vendor */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required error={!!errors.vendorID} size="small" variant="outlined"  >
              <InputLabel id="vendor-label">Vendor</InputLabel>
              <Controller
                name="vendorID"
                control={control}
                rules={{ required: "Vendor is required" }}
                render={({ field }) => (
                  <Select {...field} labelId="vendor-label" id="vendorID" label="Vendor">
                    <MenuItem value="" disabled>Select Vendor</MenuItem>
                    {vendorOptions.map(v => (
                      <MenuItem key={v.id} value={v.id}>{v.vendorName}</MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.vendorID && <FormHelperText>{errors.vendorID.message}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Bill Date */}
          <Grid item xs={12} sm={3}>
            <Controller
              name="billDate"
              control={control}
              rules={{ required: "Bill Date is required" }}
              render={() => (
                <WSTextField
                  id="billDate"
                  control={control}
                  name="billDate"
                  label="Bill Date *"
                  type={TextType.Date}
                  sx={{ color: "gray" }}
                />
              )}
            />
          </Grid>

          {/* Due Date */}
          <Grid item xs={12} sm={3}>
            <Controller
              name="dueDate"
              control={control}
              rules={{ required: "Due Date is required" }}
              render={() => (
                <WSTextField
                  id="dueDate"
                  control={control}
                  name="dueDate"
                  label="Due Date *"
                  type={TextType.Date}
                  sx={{ color: "gray" }}
                />
              )}
            />
          </Grid>
        </Grid>
        <br></br>

        {/* Items */}
        {fields.map((field, i) => (
          <Grid container spacing={2} key={field.id} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Controller
                name={`items.${i}.itemName`}
                control={control}
                rules={{ required: "Item Name is required" }}
                render={() => (
                  <WSTextField
                    id={`itemName-${i}`}
                    control={control}
                    name={`items.${i}.itemName`}
                    label="Item Name *"
                    type={TextType.Text}
                    sx={{ color: "gray" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Controller
                name={`items.${i}.qty`}
                control={control}
                render={() => (
                  <WSTextField
                    id={`qty-${i}`}
                    control={control}
                    name={`items.${i}.qty`}
                    label="Qty *"
                    type={TextType.Number}
                    sx={{ color: "gray" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Controller
                name={`items.${i}.rate`}
                control={control}
                render={() => (
                  <WSTextField
                    id={`rate-${i}`}
                    control={control}
                    name={`items.${i}.rate`}
                    label="Rate *"
                    type={TextType.Number}
                    sx={{ color: "gray" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Controller
                name={`items.${i}.tax`}
                control={control}
                render={() => (
                  <WSTextField
                    id={`tax-${i}`}
                    control={control}
                    name={`items.${i}.tax`}
                    label="Tax %"
                    type={TextType.Number}
                    sx={{ color: "gray" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={1} sm={1}>
              <IconButton onClick={() => remove(i)} disabled={fields.length === 1}>
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Button sx={{ mt: 1 ,color:"grey" }} startIcon={<AddIcon />} onClick={() => append({ itemName: "", qty: 1, rate: 0, tax: 0 })}>
          Add Item
        </Button>

        {/* Totals */}
        <Typography sx={{ fontWeight: "bold", pt: 2, pb: 1 }} color="grey">Totals</Typography>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item xs={6} sm={3}>
            <Controller
              name="subtotal"
              control={control}
              render={() => (
                <WSTextField
                  id="subtotal"
                  control={control}
                  name="subtotal"
                  label="Subtotal"
                  type={TextType.Number}
                  disabled
                  sx={{ color: "gray" }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Controller
              name="taxAmount"
              control={control}
              render={() => (
                <WSTextField
                  id="taxAmount"
                  control={control}
                  name="taxAmount"
                  label="Tax Amount"
                  type={TextType.Number}
                  disabled
                  sx={{ color: "gray" }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Controller
              name="totalAmount"
              control={control}
              render={() => (
                <WSTextField
                  id="totalAmount"
                  control={control}
                  name="totalAmount"
                  label="Total Amount"
                  type={TextType.Number}
                  disabled
                  sx={{ color: "gray" }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>
    </form>
  );
});
